// MSAL setup for Entra ID auth code + PKCE (plan §5 step 1). The tenant and SPA client id come from
// the build environment (VITE_ENTRA_TENANT_ID, VITE_ENTRA_SPA_CLIENT_ID); neither is a secret.
import type { Configuration, IPublicClientApplication } from '@azure/msal-browser';
import { LoginConfigError } from './login-error.ts';

export const ENTRA_HOST = 'login.microsoftonline.com';
export const LOGIN_SCOPES: readonly string[] = ['openid', 'profile', 'email'];
export const CALLBACK_PATH = '/auth/callback';
export const LOGIN_PATH = '/login';

/** The all-zero GUID from .env.example counts as "not set", so a copied example fails clearly. */
const PLACEHOLDER_ID = '00000000-0000-0000-0000-000000000000';

export interface EntraEnv {
  readonly VITE_ENTRA_TENANT_ID?: string | undefined;
  readonly VITE_ENTRA_SPA_CLIENT_ID?: string | undefined;
}

export interface EntraSettings {
  readonly tenantId: string;
  readonly clientId: string;
}

function usable(value: string | undefined): string | null {
  const v = value?.trim() ?? '';
  return v === '' || v === PLACEHOLDER_ID ? null : v;
}

/** Returns null when either id is missing; the login page then reports a `config` error. */
export function readEntraSettings(env: EntraEnv): EntraSettings | null {
  const tenantId = usable(env.VITE_ENTRA_TENANT_ID);
  const clientId = usable(env.VITE_ENTRA_SPA_CLIENT_ID);
  return tenantId === null || clientId === null ? null : { tenantId, clientId };
}

/**
 * Single-tenant authority, redirect back to /auth/callback. Without settings MSAL still needs a
 * well-formed config to construct; sign-in is refused before it can be used (see startLogin).
 */
export function buildMsalConfig(settings: EntraSettings | null, origin: string): Configuration {
  return {
    auth: {
      clientId: settings?.clientId ?? PLACEHOLDER_ID,
      authority: `https://${ENTRA_HOST}/${settings?.tenantId ?? 'organizations'}`,
      redirectUri: `${origin}${CALLBACK_PATH}`,
      postLogoutRedirectUri: `${origin}${LOGIN_PATH}`,
    },
    // Tab-scoped: closing the tab drops MSAL's cache; the portal session is the API cookie.
    cache: { cacheLocation: 'sessionStorage' },
  };
}

/**
 * Sends the browser to the Microsoft sign-in page. A one-time nonce from GET /auth/nonce goes into
 * the request so Entra puts it in the ID token; POST /auth/session consumes it, so a captured
 * token cannot be replayed (plan §5 step 2, review D-R2). The start page is set to the callback
 * so MSAL processes the response there instead of bouncing back to /login first.
 */
export async function startLogin(
  instance: IPublicClientApplication,
  settings: EntraSettings | null,
  origin: string,
  getNonce: () => Promise<string>,
): Promise<void> {
  if (settings === null) {
    throw new LoginConfigError('VITE_ENTRA_TENANT_ID or VITE_ENTRA_SPA_CLIENT_ID is not set');
  }
  const nonce = await getNonce();
  await instance.initialize();
  await instance.loginRedirect({
    scopes: [...LOGIN_SCOPES],
    prompt: 'select_account',
    redirectStartPage: `${origin}${CALLBACK_PATH}`,
    nonce,
  });
}

/**
 * Reads the Entra response on /auth/callback. Returns the ID token, or null when the URL carries no
 * response (someone opened the callback directly). MSAL caches the promise, so MsalProvider's own
 * call and this one share a single result.
 */
export async function completeRedirect(instance: IPublicClientApplication): Promise<string | null> {
  await instance.initialize();
  const result = await instance.handleRedirectPromise({ navigateToLoginRequestUrl: false });
  if (result === null) return null;
  // A response without an ID token is a failure, not "no response": never post an empty token.
  if (result.idToken === '') throw new Error('Entra response carried no ID token');
  return result.idToken;
}

/**
 * Drops MSAL's cached ID token and account once /auth/callback is done. The portal session is the
 * API cookie, so a token left in sessionStorage would only be something a script could lift and
 * replay against POST /auth/session from another machine until it expires.
 */
export async function forgetEntraTokens(instance: IPublicClientApplication): Promise<void> {
  await instance.clearCache();
}
